import { CalendarClock, SendHorizontal, TriangleAlert, X } from "lucide-react";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotify } from "ra-core";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { EmptyState } from "../data-display/EmptyState";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";

interface Scheduled {
  id: string;
  conversation_id: string;
  contact_id: number;
  contact_name: string;
  channel: string;
  body: string;
  subject: string | null;
  scheduled_at: string;
  will_be_suppressed: boolean;
  created_by_name: string | null;
}

const inputClass =
  "h-8 rounded-md border border-input bg-transparent px-2 text-[12.5px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

/** Local `YYYY-MM-DDTHH:mm` for a datetime-local input. */
const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

/**
 * Everything queued to go out later.
 *
 * Before this, a reminder scheduled for Tuesday was invisible until it sent or
 * failed, the only way to stop one was to spot it in the thread and hope you
 * got there first.
 *
 * A row whose recipient has since opted out is flagged here rather than at
 * dispatch: the consent gate will refuse it either way, and finding out now
 * beats wondering later why it never arrived.
 */
export const ScheduledMessages = () => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const workspaceId = getActiveWorkspaceId();
  const [editing, setEditing] = useState<string | null>(null);
  const [when, setWhen] = useState("");

  const { data, isPending, refetch } = useQuery<Scheduled[]>({
    queryKey: ["scheduled-messages", workspaceId],
    enabled: Boolean(workspaceId),
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("scheduled_messages")
        .select("*")
        .eq("workspace_id", workspaceId!)
        .order("scheduled_at", { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as Scheduled[];
    },
  });

  const after = () => {
    refetch();
    queryClient.invalidateQueries({ queryKey: ["inbox"] });
  };

  const cancel = async (row: Scheduled) => {
    const { error } = await getSupabaseClient().rpc(
      "cancel_scheduled_message",
      { p_message_id: row.id },
    );
    if (error) {
      notify(error.message, { type: "warning" });
      after();
      return;
    }
    // Kept as an unsent draft rather than deleted, someone wrote it.
    notify("Cancelled. The text is kept as a draft on the conversation.");
    after();
  };

  /**
   * Release a scheduled message immediately.
   *
   * `reschedule_message` only moves a message later, so before this the only
   * way to say "actually, send that now" was to cancel and retype it, losing
   * the original text and who wrote it. Consent is re-checked server-side, so
   * a contact who opted out since scheduling is still protected here.
   */
  const sendNow = async (row: Scheduled) => {
    const { data, error } = await getSupabaseClient().rpc(
      "send_scheduled_message_now",
      { p_message_id: row.id },
    );
    if (error) {
      notify(error.message, { type: "warning" });
      after();
      return;
    }
    const result = data as { status: string; error: string | null } | null;
    if (result?.status === "failed") {
      notify(result.error ?? "That message could not be sent.", {
        type: "warning",
      });
    } else {
      notify("Sending now");
    }
    after();
  };

  const reschedule = async (row: Scheduled) => {
    const { error } = await getSupabaseClient().rpc("reschedule_message", {
      p_message_id: row.id,
      p_send_at: new Date(when).toISOString(),
    });
    if (error) {
      notify(error.message, { type: "warning" });
      return;
    }
    notify("Rescheduled");
    setEditing(null);
    after();
  };

  const rows = data ?? [];

  if (isPending) {
    return (
      <p className="py-12 text-center text-[13px] text-muted-foreground">
        Loading scheduled messages…
      </p>
    );
  }

  if (rows.length === 0) {
    return (
      <Card className="py-16">
        <EmptyState
          icon={CalendarClock}
          title="Nothing scheduled"
          description="Messages you schedule for later will appear here, where you can reschedule or cancel them."
        />
      </Card>
    );
  }

  return (
    <ul className="flex flex-col gap-2">
      {rows.map((row) => (
        <li key={row.id}>
          <Card className="flex flex-wrap items-start gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <Link
                  to={`/contacts/${row.contact_id}/show`}
                  className="truncate text-[13.5px] font-medium text-foreground no-underline hover:underline"
                >
                  {row.contact_name || "Unknown contact"}
                </Link>
                <span className="text-[11.5px] uppercase text-muted-foreground">
                  {row.channel}
                </span>
                <span className="text-[12px] text-muted-foreground">
                  {new Date(row.scheduled_at).toLocaleString()}
                </span>
              </div>
              <p className="mt-0.5 line-clamp-2 text-[12.5px] text-muted-foreground">
                {row.subject ? `${row.subject}, ` : ""}
                {row.body}
              </p>
              {row.will_be_suppressed ? (
                <p className="mt-1 flex items-center gap-1 text-[12px] text-amber-700">
                  <TriangleAlert className="size-3.5 shrink-0" />
                  This contact has opted out, this will not be delivered.
                </p>
              ) : null}
              {row.created_by_name ? (
                <p className="text-[11.5px] text-muted-foreground">
                  Scheduled by {row.created_by_name}
                </p>
              ) : null}
            </div>

            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {editing === row.id ? (
                <>
                  <input
                    type="datetime-local"
                    className={inputClass}
                    value={when}
                    onChange={(event) => setWhen(event.target.value)}
                  />
                  <Button size="sm" onClick={() => reschedule(row)}>
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditing(null)}
                  >
                    <X className="size-3.5" />
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8"
                    disabled={row.will_be_suppressed}
                    title={
                      row.will_be_suppressed
                        ? "This contact has opted out"
                        : undefined
                    }
                    onClick={() => sendNow(row)}
                  >
                    <SendHorizontal className="mr-1 size-3.5" />
                    Send now
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8"
                    onClick={() => {
                      setEditing(row.id);
                      setWhen(toLocalInput(row.scheduled_at));
                    }}
                  >
                    Reschedule
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 text-muted-foreground hover:text-destructive"
                    onClick={() => cancel(row)}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </div>
          </Card>
        </li>
      ))}
    </ul>
  );
};
